export const formatPreFinanceMoney = (value) => {
  if (value === null || value === undefined) {
    return "—";
  }

  return Number(value).toLocaleString("nl-NL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const formatPreFinancePeriod = ({ year, startMonth, endMonth, periodType }) => {
  if (!year || !startMonth || !endMonth) {
    return "—";
  }

  const months =
    startMonth === endMonth
      ? String(startMonth).padStart(2, "0")
      : `${String(startMonth).padStart(2, "0")}–${String(endMonth).padStart(2, "0")}`;

  if (periodType === 2) {
    const quarter = Math.floor((startMonth - 1) / 3) + 1;
    return `${year} Q${quarter} ${months} p/Q`;
  }

  return `${year} ${months} p/M`;
};

export const groupVatSourcesBySupplier = (sources = []) => {
  const suppliers = new Map();

  sources.forEach((source) => {
    const supplierKey = source.supplierId ?? 0;
    const currentSupplier = suppliers.get(supplierKey) || {
      supplierId: source.supplierId,
      supplierName: source.supplierName || `Supplier ${source.supplierId ?? "unknown"}`,
      sources: [],
      totalGrossAmount: 0,
      totalDeclarationAmount: 0,
      totalRefundAmount: 0,
      totalVatAmountEur: 0,
    };

    currentSupplier.sources.push(source);
    currentSupplier.totalGrossAmount += Number(source.grossAmount || 0);
    currentSupplier.totalDeclarationAmount += Number(source.declarationAmount || 0);
    currentSupplier.totalRefundAmount += Number(source.refundAmount || 0);
    currentSupplier.totalVatAmountEur += Number(source.vatAmountEur || 0);
    suppliers.set(supplierKey, currentSupplier);
  });

  return Array.from(suppliers.values()).sort((left, right) =>
    left.supplierName.localeCompare(right.supplierName)
  );
};

export const calculateSelectedVatSummary = (groups = [], selectedGroupKeys = []) => {
  const selectedKeys = new Set(selectedGroupKeys);
  const selectedGroups = groups.filter(
    (group) => group.isSelectable && selectedKeys.has(group.groupKey)
  );

  const summary = selectedGroups.reduce(
    (summary, group) => ({
      groupCount: summary.groupCount + 1,
      sourceCount: summary.sourceCount + group.sources.length,
      missingFileCount: summary.missingFileCount + group.missingSourceFileCount,
      totalGrossAmount: summary.totalGrossAmount + Number(group.totalGrossAmount || 0),
      totalDeclarationAmount:
        summary.totalDeclarationAmount + Number(group.declarationAmount || 0),
      totalRefundAmount: summary.totalRefundAmount + Number(group.refundAmount || 0),
      totalVatAmountEur: summary.totalVatAmountEur + Number(group.totalVatAmountEur || 0),
      totalRefundFee: summary.totalRefundFee + Number(group.refundFee || 0),
      totalVatOnDeclarationFee:
        summary.totalVatOnDeclarationFee + Number(group.vatOnDeclarationFee || 0),
      totalCosts: summary.totalCosts + Number(group.totalCosts || 0),
      totalPayoutAmount: summary.totalPayoutAmount + Number(group.payoutAmount || 0),
    }),
    {
      groupCount: 0,
      sourceCount: 0,
      missingFileCount: 0,
      totalGrossAmount: 0,
      totalDeclarationAmount: 0,
      totalRefundAmount: 0,
      totalVatAmountEur: 0,
      totalRefundFee: 0,
      totalVatOnDeclarationFee: 0,
      totalCosts: 0,
      totalPayoutAmount: 0,
    }
  );

  summary.hasMixedCurrencies =
    new Set(
      selectedGroups
        .map((group) => group.localCurrencyId)
        .filter((currencyId) => currencyId !== null && currencyId !== undefined)
    ).size > 1;
  summary.vfPercentages = Array.from(
    new Set(
      selectedGroups
        .map((group) => group.appliedVfPercentage)
        .filter((percentage) => percentage !== null && percentage !== undefined)
    )
  ).sort((left, right) => left - right);
  return summary;
};

export const buildVatCreateRequest = ({
  entityId,
  countryId,
  year,
  monthFrom,
  monthTo,
  excludedSourceIds,
  groups,
  selectedGroupKeys,
  groupOptions,
}) => {
  const selectedKeys = new Set(selectedGroupKeys);

  return {
    entityId: Number(entityId),
    preFinanceType: 0,
    countryId: countryId ? Number(countryId) : null,
    year: Number(year),
    monthFrom: Number(monthFrom),
    monthTo: Number(monthTo),
    excludedSourceIds: [...excludedSourceIds].sort((left, right) => left - right),
    groups: groups
      .filter((group) => group.isSelectable && selectedKeys.has(group.groupKey))
      .map((group) => ({
        groupKey: group.groupKey,
        previewFingerprint: group.previewFingerprint,
        continueExistingPeriod: Boolean(groupOptions[group.groupKey]?.continueExistingPeriod),
        optionalClaimId: null,
      })),
  };
};

export const calculateSelectedExciseSummary = (groups = [], selectedGroupKeys = []) => {
  const selectedKeys = new Set(selectedGroupKeys);
  const selectedGroups = groups.filter(
    (group) => group.isSelectable && selectedKeys.has(group.groupKey)
  );

  const summary = selectedGroups.reduce(
    (summary, group) => ({
      groupCount: summary.groupCount + 1,
      sourceCount: summary.sourceCount + group.sources.length,
      missingFileCount: summary.missingFileCount + group.missingSourceFileCount,
      totalLiters: summary.totalLiters + Number(group.totalLiters || 0),
      totalRefundAmount: summary.totalRefundAmount + Number(group.refundAmount || 0),
      totalDeclarationAmount:
        summary.totalDeclarationAmount + Number(group.declarationAmount || 0),
      totalRefundFee: summary.totalRefundFee + Number(group.refundFee || 0),
      totalVatOnDeclarationFee:
        summary.totalVatOnDeclarationFee + Number(group.vatOnDeclarationFee || 0),
      totalCosts: summary.totalCosts + Number(group.totalCosts || 0),
      totalPayoutAmount: summary.totalPayoutAmount + Number(group.payoutAmount || 0),
      previewCodes: summary.previewCodes.concat(group.previewPreFinanceCode || group.groupKey),
    }),
    {
      groupCount: 0,
      sourceCount: 0,
      missingFileCount: 0,
      totalLiters: 0,
      totalRefundAmount: 0,
      totalDeclarationAmount: 0,
      totalRefundFee: 0,
      totalVatOnDeclarationFee: 0,
      totalCosts: 0,
      totalPayoutAmount: 0,
      previewCodes: [],
    }
  );

  summary.vfPercentages = Array.from(
    new Set(
      selectedGroups
        .map((group) => group.appliedVfPercentage)
        .filter((percentage) => percentage !== null && percentage !== undefined)
    )
  ).sort((left, right) => left - right);

  return summary;
};

export const buildExciseCreateRequest = ({
  entityId,
  countryId,
  year,
  monthFrom,
  monthTo,
  excludedSourceIds,
  groups,
  selectedGroupKeys,
  groupOptions,
}) => {
  const selectedKeys = new Set(selectedGroupKeys);

  return {
    entityId: Number(entityId),
    preFinanceType: 1,
    countryId: countryId ? Number(countryId) : null,
    year: Number(year),
    monthFrom: Number(monthFrom),
    monthTo: Number(monthTo),
    excludedSourceIds: [...excludedSourceIds].sort((left, right) => left - right),
    groups: groups
      .filter((group) => group.isSelectable && selectedKeys.has(group.groupKey))
      .map((group) => {
        const optionalClaimId = groupOptions[group.groupKey]?.optionalClaimId;
        return {
          groupKey: group.groupKey,
          previewFingerprint: group.previewFingerprint,
          continueExistingPeriod: Boolean(groupOptions[group.groupKey]?.continueExistingPeriod),
          optionalClaimId:
            optionalClaimId === null || optionalClaimId === undefined || optionalClaimId === ""
              ? null
              : Number(optionalClaimId),
        };
      }),
  };
};
